import type { ImgHTMLAttributes } from "react"

/**
 * Image configurations based on next/image
 *
 * @see https://github.com/vercel/next.js/blob/201ab71ee74b58484806c13b794023965210967b/packages/next/src/shared/lib/image-config.ts#L55
 */
type ImageConfigComplete = {
  deviceSizes: number[]
  imageSizes: number[]
}

type ImageConfig = ImageConfigComplete & {
  allSizes: number[]
}

export interface ImageProps extends SrcProps, Omit<ImgHTMLAttributes<HTMLImageElement>, "alt"> {
  span?: number
  /** span_sm 640px and up */
  span_sm?: number
  /** span_md 768px and up */
  span_md?: number
  /** span_lg 1024px and up */
  span_lg?: number
  /** span_xl 1280px and up */
  span_xl?: number
  /** span_2xl 1536px and up */
  span_2xl?: number
  /** full_width is used for other reason */
  full_width?: boolean
  alt?: string | null
}

interface ConfigProps {
  deviceSizes: number[]
  imageSizes: number[]
  breakpoints: Record<string, string>
  container: {
    width: number
    padding: number
  }
  grid: {
    columns: number
  }
}

export const config: ConfigProps = {
  // https://nextjs.org/docs/basic-features/image-optimization#device-sizes
  deviceSizes: [375, 640, 750, 828, 1080, 1200, 1920],
  // https://nextjs.org/docs/basic-features/image-optimization#image-sizes
  // The reason there are two separate lists is that imageSizes is only used for images which provide a sizes prop, which indicates that the image is less than the full width of the screen.
  // Therefore, the sizes in imageSizes should all be smaller than the smallest size in deviceSizes.
  imageSizes: [16, 32, 48, 64, 96, 128, 256],
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
  container: {
    width: 1280,
    padding: 16,
  },
  grid: {
    columns: 12,
  },
}

export const getSizesAttribute = (
  span = 12,
  spanMd?: number,
  spanSm?: number,
  spanLg?: number,
  spanXl?: number,
  span2xl?: number,
  fullWidth = false
): string => {
  let sizes = ""
  const colSpanBreakpoints: { [key: string]: number } = {}

  // save all the provided colSpan attributes in to the object
  colSpanBreakpoints["default"] = span
  if (spanSm) colSpanBreakpoints["sm"] = spanSm
  if (spanMd) colSpanBreakpoints["md"] = spanMd
  if (spanLg) colSpanBreakpoints["lg"] = spanLg
  if (spanXl) colSpanBreakpoints["xl"] = spanXl
  if (span2xl) colSpanBreakpoints["2xl"] = span2xl

  const containerPlusPadding = config.container.width + config.container.padding * 2
  if (fullWidth) {
    // if fullWidth="true", we don't need fancy calc stuff, as the container is always fullscreen and we can determine the size based on the viewport
    for (const [key, value] of Object.entries(colSpanBreakpoints)) {
      if (key === "default") {
        // do nothing, that gets pushed automatically at the end
      } else {
        sizes += `(min-width: ${config.breakpoints[key]}) calc((100vw / ${config.grid.columns}) * ${value}), `
      }
      // the default sizes attribute with no media query
      sizes += `calc((100vw / ${config.grid.columns}) * ${value})`
    }

    return sizes
  } else {
    // lastValue stores the col span of the last breakpoint, defaults to 12
    let lastBreakpointColSpan = span
    // step through all the provided colSpanBreakpoints and add the media queries to the sizes string
    for (const [key, value] of Object.entries(colSpanBreakpoints)) {
      if (key === "default") {
        // do nothing, that gets pushed automatically at the end
      } else {
        // check if the breakpoint is bigger then the containers max width, if not we can calculate the size based on the viewport and not from the container max width
        const bpBiggerThanMaxWidth = config.container.width - value > 0 ? "100vw" : config.container.width
        sizes += `(min-width: ${config.breakpoints[key]}) calc((${bpBiggerThanMaxWidth} - ${
          config.container.padding * 2
        }px) / ${config.grid.columns} * ${value}), `
      }
      lastBreakpointColSpan = value
    }

    sizes += `(min-width: ${containerPlusPadding}px) calc(${config.container.width}px / ${config.grid.columns} * ${lastBreakpointColSpan}), `
    // the default sizes attribute with no media query
    sizes += `calc(((100vw - ${config.container.padding * 2}px) / ${config.grid.columns} ) * ${span})`
    return sizes
  }
}

/**
 * Based on next/image
 * @see https://github.com/vercel/next.js/blob/5451564f364399003b05939fa6dd7d32c1dabec7/packages/next/src/client/image.tsx#L160
 */
export const getWidths = (
  width: number | undefined,
  sizes: string | undefined
): { widths: number[]; kind: "w" | "x" } => {
  // Generate image sizes
  const imageConfig: ImageConfig = {
    ...config,
    allSizes: [...config.deviceSizes, ...config.imageSizes].sort((a, b) => a - b),
    deviceSizes: config.deviceSizes.sort((a, b) => a - b),
  }

  if (sizes) {
    // Find all the "vw" percent sizes used in the sizes prop
    const viewportWidthRe = /(^|\s)(1?\d?\d)vw/g
    const percentSizes = []
    for (let match; (match = viewportWidthRe.exec(sizes)); match) {
      percentSizes.push(parseInt(match[2]))
    }
    if (percentSizes.length) {
      const smallestRatio = Math.min(...percentSizes) * 0.01
      return {
        widths: imageConfig.allSizes.filter((s) => s >= imageConfig.deviceSizes[0] * smallestRatio),
        kind: "w",
      }
    }
    return { widths: imageConfig.allSizes, kind: "w" }
  }
  if (typeof width !== "number") {
    return { widths: imageConfig.deviceSizes, kind: "w" }
  }

  const widths = [
    ...new Set(
      // > This means that most OLED screens that say they are 3x resolution,
      // > are actually 3x in the green color, but only 1.5x in the red and
      // > blue colors. Showing a 3x resolution image in the app vs a 2x
      // > resolution image will be visually the same, though the 3x image
      // > takes significantly more data. Even true 3x resolution screens are
      // > wasteful as the human eye cannot see that level of detail without
      // > something like a magnifying glass.
      // https://blog.twitter.com/engineering/en_us/topics/infrastructure/2019/capping-image-fidelity-on-ultra-high-resolution-devices.html
      [width, width * 2 /*, width * 3*/].map(
        (w) => imageConfig.allSizes.find((p) => p >= w) || imageConfig.allSizes[imageConfig.allSizes.length - 1]
      )
    ),
  ]
  return { widths, kind: "x" }
}

const loader = ({ src, width }: { src: string; width: number | undefined }): string => {
  return `${import.meta.env.PUBLIC_IMAGE_CDN_URL}${src}/resize=${width}`
}

export const getSrcSet = ({ src, widths, kind }: { src: string; widths: number[]; kind: "w" | "x" }): string => {
  return widths
    .map(
      (w, i) =>
        `${loader({
          src,
          width: w,
        })} ${kind === "w" ? w : i + 1}${kind}`
    )
    .join(", ")
}

export const getCoordinatesFromFocus = (focus: string | null, in_percent = false): { x: number; y: number } => {
  const splitFocalValues = focus?.split("-") ?? []
  const divisor = in_percent ? 100 : 1

  return {
    x: parseInt(splitFocalValues.at(0) ?? "50", 10) / divisor,
    y: parseInt(splitFocalValues.at(1) ?? "50", 10) / divisor,
  }
}

export const getFocusFromCoordinates = (x: number | null, y: number | null): string => {
  return `${((x ?? 0.5) * 100).toFixed(0)}-${((y ?? 0.5) * 100).toFixed(0)}-1`
}

const parseCMSFocalPoints = (focus: string) => {
  const { x, y } = getCoordinatesFromFocus(focus)

  return `${x}px${y}p`
}

export interface SrcProps extends Pick<ImgHTMLAttributes<HTMLImageElement>, "src" | "width" | "height"> {
  /** focal_point_x  */
  focal_point_x?: number
  /** focal_point_y  */
  focal_point_y?: number
  /** focal_points coming from CMS */
  focal_points?: string | null
  /** aspect_ratio e.g. `16:9`  */
  aspect_ratio?: string
  /** image quality, defaults to 80 */
  quality?: number
  fill?: boolean
  /** use contain instead of cover to avoid cropping images */
  contain?: boolean
}

export const srcString = (props: SrcProps) => {
  // Destructure to filter out custom props, pass rest to the image element
  const {
    src = "/general/blank.jpg", // Fallback image
    focal_point_x,
    focal_point_y,
    focal_points,
    quality,
    contain = false,
  } = props

  // Apply twic specific transformations
  const transformations: Record<string, string | number | null> = {
    quality: quality ?? 80,
    ...(focal_point_x && focal_point_y ? { focus: `${focal_point_x * 100}px${focal_point_y * 100}p` } : {}),
    ...(focal_points ? { focus: parseCMSFocalPoints(focal_points) } : {}),
    ...(props.aspect_ratio ? { cover: props.aspect_ratio } : {}),
    ...(props.width && props.height
      ? contain
        ? { contain: `${props.width}x${props.height}` }
        : { cover: `${props.width}x${props.height}` }
      : {}),
  }
  const transformationsString = Object.keys(transformations)
    .map((key) => `${key}=${transformations[key]}`)
    .join("/")

  const srcWithoutDomain = src.replace(/https:\/\/([^/]+)/, "")

  return `${srcWithoutDomain}?twic=v1/${transformationsString}`
}

/**
 * This component is based on the next/image component with a custom function to generate the sizes.
 * It also handles the TwicPics transformations.
 *
 * @see https://github.com/vercel/next.js/blob/5451564f364399003b05939fa6dd7d32c1dabec7/packages/next/src/client/image.tsx
 */
const Image = (props: ImageProps): JSX.Element => {
  // Destructure to filter out custom props, pass rest to the image element
  const {
    src = "/general/blank.jpg", // Fallback image
    span,
    span_sm,
    span_md,
    span_lg,
    span_xl,
    span_2xl,
    full_width,
    focal_point_x,
    focal_point_y,
    focal_points,
    aspect_ratio,
    quality,
    fill = false,
    contain,
    alt,
    ...rest
  } = props

  const _src = srcString(props)
  const isBase64 = src.startsWith("data:")

  const sizes = getSizesAttribute(span, span_sm, span_md, span_lg, span_xl, span_2xl, full_width)
  const { widths, kind } = getWidths(props.width ? Number(props.width) : undefined, props.width ? undefined : sizes)
  const srcSet = getSrcSet({ src: _src, widths, kind })

  return (
    <img
      style={{ ...(fill && { position: "absolute", height: "100%", width: "100%", inset: "0px" }) }}
      {...rest}
      sizes={sizes}
      alt={alt ?? undefined}
      loading={props.loading ?? "lazy"}
      srcSet={isBase64 ? undefined : srcSet}
      src={
        isBase64
          ? src
          : loader({
              src: _src,
              width: widths.at(-1),
            })
      }
    />
  )
}

export default Image
